import React, { useState } from 'react';
import { discussionAPI } from '../../services/api';

const MAX_CHARS = 5000;

export default function PostComposer({
  sceneId: tutorialId,
  parentId = null,
  onPostCreated,
  onCancel,
  placeholder = 'Share your thoughts…',
}) {
  const [body, setBody] = useState('');
  const [preview, setPreview] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [postStatus, setPostStatus] = useState(null);

  const charCount = body.length;
  const overLimit = charCount > MAX_CHARS;

  const handleSubmit = async () => {
    if (!body.trim() || overLimit || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await discussionAPI.createPost(tutorialId, body.trim(), parentId);
      const post = res.data;
      if (post?.visibility === 'pending') {
        setPostStatus('pending');
      } else if (post?.visibility === 'rejected') {
        setPostStatus('rejected');
        setError(post.moderation_reason || 'Your post was not approved.');
      } else {
        setPostStatus('visible');
      }
      setBody('');
      onPostCreated?.();
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to create post.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={containerStyle}>
      <div style={tabBarStyle}>
        <button
          onClick={() => setPreview(false)}
          style={tabStyle(!preview)}
        >
          Write
        </button>
        <button
          onClick={() => setPreview(true)}
          style={tabStyle(preview)}
        >
          Preview
        </button>
      </div>

      {preview ? (
        <div style={previewStyle}>
          {body || <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>Nothing to preview</span>}
        </div>
      ) : (
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={placeholder}
          rows={4}
          style={textareaStyle}
          maxLength={MAX_CHARS + 100}
        />
      )}

      <div style={footerStyle}>
        <span style={{ fontSize: 12, color: overLimit ? '#dc2626' : '#94a3b8' }}>
          {charCount}/{MAX_CHARS}
        </span>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {postStatus === 'pending' && (
            <span style={pendingBadge}>Pending moderation</span>
          )}
          {postStatus === 'rejected' && (
            <span style={rejectedBadge}>Rejected</span>
          )}
          <button onClick={onCancel} style={cancelBtnStyle}>
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!body.trim() || overLimit || submitting}
            style={submitBtnStyle(!body.trim() || overLimit || submitting)}
          >
            {submitting ? 'Posting…' : parentId ? 'Reply' : 'Post'}
          </button>
        </div>
      </div>

      {error && <div style={errorStyle}>{error}</div>}
    </div>
  );
}

const containerStyle = {
  border: '1px solid #e2e8f0',
  borderRadius: 10,
  padding: 12,
  marginBottom: 12,
  background: '#fff',
};
const tabBarStyle = {
  display: 'flex',
  gap: 4,
  marginBottom: 8,
};
const tabStyle = (active) => ({
  background: active ? '#1e293b' : 'transparent',
  color: active ? '#fff' : '#64748b',
  border: 'none',
  borderRadius: 4,
  padding: '3px 10px',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
});
const textareaStyle = {
  width: '100%',
  padding: 10,
  border: '1px solid #e2e8f0',
  borderRadius: 8,
  fontSize: 14,
  fontFamily: 'inherit',
  lineHeight: 1.5,
  resize: 'vertical',
  boxSizing: 'border-box',
  minHeight: 80,
};
const previewStyle = {
  padding: 10,
  border: '1px solid #e2e8f0',
  borderRadius: 8,
  fontSize: 14,
  lineHeight: 1.6,
  minHeight: 80,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  color: '#1f2937',
  background: '#f8fafc',
};
const footerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: 8,
};
const cancelBtnStyle = {
  background: 'transparent',
  border: '1px solid #d1d5db',
  borderRadius: 6,
  padding: '5px 12px',
  fontSize: 13,
  color: '#64748b',
  cursor: 'pointer',
};
const submitBtnStyle = (disabled) => ({
  background: disabled ? '#94a3b8' : '#3b82f6',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  padding: '5px 14px',
  fontSize: 13,
  fontWeight: 600,
  cursor: disabled ? 'default' : 'pointer',
});
const pendingBadge = {
  fontSize: 11,
  fontWeight: 600,
  color: '#92400e',
  background: '#fef3c7',
  padding: '2px 8px',
  borderRadius: 999,
};
const rejectedBadge = {
  fontSize: 11,
  fontWeight: 600,
  color: '#991b1b',
  background: '#fee2e2',
  padding: '2px 8px',
  borderRadius: 999,
};
const errorStyle = {
  marginTop: 8,
  fontSize: 12,
  color: '#dc2626',
  background: 'rgba(239,68,68,0.08)',
  padding: '6px 10px',
  borderRadius: 6,
};
